import { useMemo } from 'react'
import { useDashboardStore } from '@/state/useDashboardStore'
import { useSettingsStore } from '@/state/useSettingsStore'
import { OsmMap, type MapBurst, type MapPoint } from '@/components/map/OsmMap'
import { formatRelativeAge } from '@/lib/format'
import { now } from '@/lib/clock'

/**
 * Mapa objednávek za dnešní den. Den začíná ve 4:00 ráno, takže noční provoz
 * po půlnoci se ještě počítá k předchozímu dni a mapa se pak vynuluje.
 */

// Každá objednávka je vlastní špendlík. Přesné souřadnice objednávek nemáme
// (event nese jen pobočku), proto se rozprostřou po spirále kolem pobočky —
// deterministicky, aby při dalším načtení nepřeskakovaly jinam.
const PIN_RADIUS = 7
// Přes den objednávek přibývá; nad tímhle počtem už jsou body stejně přes
// sebe a jen zatěžují vykreslování. Součet nahoře zůstává přesný.
const MAX_PINS = 120
const SPREAD_DEG = 0.055
// Zlatý úhel rozloží body rovnoměrně, místo aby se srovnaly do řady.
const GOLDEN_ANGLE = 2.399963

function scatter(lat: number, lng: number, i: number, total: number) {
  if (total <= 1) return { lat, lng }
  const angle = i * GOLDEN_ANGLE
  const dist = SPREAD_DEG * Math.sqrt((i + 1) / total)
  // Poledníky se u nás sbíhají, takže délku roztahujeme podle šířky.
  return {
    lat: lat + dist * Math.sin(angle),
    lng: lng + (dist * Math.cos(angle)) / Math.cos((lat * Math.PI) / 180),
  }
}

export function OrdersLiveSlide() {
  const snapshot = useDashboardStore((s) => s.snapshot)
  const locale = useSettingsStore((s) => s.locale)

  const liveActivity = useDashboardStore((s) => s.liveActivity)
  const orders = snapshot?.ordersToday ?? []

  // Efekt se přehraje u pobočky, ze které právě přišla objednávka. Bere se
  // ze živého proudu, takže se objeví dřív, než ji potvrdí snapshot.
  const bursts = useMemo<MapBurst[]>(() => {
    if (!snapshot) return []
    const out: MapBurst[] = []
    for (const e of liveActivity) {
      if (e.eventName !== 'usage.order.created') continue
      const venue = snapshot.venueMap.find((v) => v.id === e.venueId)
      if (!venue) continue
      out.push({ id: e.id, lat: venue.lat, lng: venue.lng, label: venue.name })
    }
    return out
  }, [liveActivity, snapshot])

  const points = useMemo<MapPoint[]>(() => {
    const out: MapPoint[] = []
    // Pobočka bez souřadnic nemá kam nakreslit bod. Ze součtů se ale
    // nevyhazuje, jinak by nesedělo, kolik objednávek dnes bylo.
    const placed = orders.filter((o) => o.lat != null && o.lng != null)
    const totalOrders = placed.reduce((sum, o) => sum + o.orders, 0)
    // Poměrné zkrácení, aby si pobočky zachovaly vzájemný poměr.
    const ratio = totalOrders > MAX_PINS ? MAX_PINS / totalOrders : 1
    for (const o of placed) {
      const pins = Math.max(1, Math.round(o.orders * ratio))
      for (let i = 0; i < pins; i += 1) {
        const pos = scatter(o.lat as number, o.lng as number, i, pins)
        out.push({
          id: `${o.id}-${i}`,
          name: o.name,
          city: o.city,
          lat: pos.lat,
          lng: pos.lng,
          colorVar: 'signal-good',
          radius: PIN_RADIUS,
          variant: 'pin',
          badge: locale === 'cs' ? `${o.orders} objednávek dnes` : `${o.orders} orders today`,
        })
      }
    }
    return out
  }, [orders, locale])

  // Log nových objednávek. Živé jdou navrch, pod nimi potvrzené ze skladu —
  // jinak by seznam zmizel ve chvíli, kdy snapshot živou položku nahradí.
  const orderLog = (() => {
    if (!snapshot) return []
    const live = liveActivity.filter((e) => e.eventName === 'usage.order.created')
    const batch = snapshot.recentActivity.filter((e) => e.eventName === 'usage.order.created')
    const seen = new Set<string>()
    return [...live, ...batch]
      .filter((e) => (seen.has(e.id) ? false : (seen.add(e.id), true)))
      .slice(0, 10)
  })()

  if (!snapshot) return null

  const cs = locale === 'cs'
  const total = orders.reduce((sum, o) => sum + o.orders, 0)
  const unplacedOrders = orders
    .filter((o) => o.lat == null || o.lng == null)
    .reduce((sum, o) => sum + o.orders, 0)
  // Pobočka s nejvíc objednávkami dneška.
  const top = orders.reduce<(typeof orders)[number] | null>(
    (best, o) => (best == null || o.orders > best.orders ? o : best),
    null,
  )


  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <div className="grid grid-cols-3 gap-4">
        <Tile value={String(total)} label={cs ? 'Objednávek dnes' : 'Orders today'} accent />
        <Tile
          value={String(orders.length)}
          label={cs ? 'Poboček s objednávkou' : 'Venues with an order'}
        />
        <Tile
          value={top ? String(top.orders) : '—'}
          label={
            top
              ? `${cs ? 'Nejvíc: ' : 'Top: '}${top.name}`
              : cs
                ? 'Nejvíce objednávek'
                : 'Most orders'
          }
        />
      </div>

      <section className="relative min-h-0 flex-1 overflow-hidden rounded-3xl border border-border bg-surface-1">
        <OsmMap points={points} bursts={bursts} className="absolute inset-0 h-full w-full" />

        {unplacedOrders > 0 && (
          <div className="pointer-events-none absolute bottom-3 left-3 z-[500] rounded-xl border border-border bg-surface-1/90 px-3 py-1.5 text-xs text-ink-subtle">
            {cs
              ? `${unplacedOrders} objednávek z poboček bez souřadnic`
              : `${unplacedOrders} orders from venues without coordinates`}
          </div>
        )}


        {orderLog.length > 0 && (
          <div className="pointer-events-none absolute right-3 top-3 z-[500] w-72 overflow-hidden rounded-2xl border border-border-strong/60 bg-surface-0/95 shadow-[0_8px_32px_rgba(0,0,0,0.55)]">
            <div className="flex items-center justify-between border-b border-border/70 px-3 py-2">
              <span className="text-[10px] uppercase tracking-[0.16em] text-ink-subtle">
                {cs ? 'Nové objednávky' : 'New orders'}
              </span>
              <span className="inline-flex items-center gap-1 text-[10px] text-ink-subtle">
                <span className="size-1.5 animate-pulse rounded-full bg-signal-good" />
                live
              </span>
            </div>
            <ul className="flex flex-col">
              {orderLog.map((e) => (
                <li
                  key={e.id}
                  className="flex items-center gap-2 border-b border-border/40 px-3 py-1.5 last:border-b-0"
                >
                  <span className="size-1.5 shrink-0 rounded-full bg-[var(--signal-good)]" />
                  <span className="min-w-0 flex-1 truncate text-xs text-ink" title={e.venueName}>
                    {e.venueName}
                  </span>
                  <span className="shrink-0 text-[10px] tabular-nums text-ink-subtle">
                    {formatRelativeAge(e.at, locale, now())}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {orders.length === 0 && (
          <div className="pointer-events-none absolute inset-0 z-[500] flex items-center justify-center">
            <div className="rounded-2xl border border-border bg-surface-1/90 px-6 py-4 text-center">
              <div className="font-display text-xl font-semibold text-ink">
                {cs ? 'Zatím žádné objednávky' : 'No orders yet'}
              </div>
              <div className="mt-1 text-xs text-ink-subtle">
                {cs ? 'Den začal ve 4:00' : 'The day started at 4:00'}
              </div>
            </div>
          </div>
        )}
      </section>

      {orders.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {orders.slice(0, 6).map((o) => (
            <div
              key={o.id}
              className="flex items-baseline gap-2 rounded-xl border border-border bg-surface-1/70 px-3 py-1.5"
            >
              <span className="font-display text-lg font-semibold text-ink">{o.orders}</span>
              <span className="truncate text-xs text-ink-subtle">{o.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function Tile({ value, label, accent }: { value: string; label: string; accent?: boolean }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-border bg-surface-1/70 px-5 py-3">
      <span
        className="size-3 shrink-0 rounded-full"
        style={{ backgroundColor: accent ? 'var(--signal-good)' : 'var(--brand-primary)' }}
      />
      <div className="min-w-0">
        <div className="font-display text-2xl font-semibold text-ink">{value}</div>
        <div className="truncate text-xs uppercase tracking-[0.14em] text-ink-subtle">{label}</div>
      </div>
    </div>
  )
}
